import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { faCalendarRange } from '@fortawesome/pro-regular-svg-icons/faCalendarRange';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Col, type TableColumnsType } from 'antd';
import { PosCard } from 'pos-client/components/shared/Display/Card/PosCard.tsx';
import { PosTable } from 'pos-client/components/shared/Display/Table/PosTable.tsx';
import { useTableData } from 'pos-client/hooks/useTableData.ts';
import { CreateEditTradingTimesModal } from 'pos-client/pages/locations/Modals/CreateEditTradingTimesModal.tsx';
import {
  createTableFilters,
  filterByProperty,
  sortTimes,
  sortWeekdays,
} from 'pos-client/utils/dataTableHelpers.tsx';
import { clearSeconds } from 'pos-client/utils/dateHelpers.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import type { TradingTimesModel } from 'pos-shared/models/openingHoursGroupSchema.ts';

export const TradingTimes = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { data, isLoading } = trpc.getAllTradingTimes.useQuery({
    id: id as string,
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalData, setModalData] = useState<
    Partial<TradingTimesModel> | undefined
  >();
  const tableData = useTableData(data, (x) => x.id);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const showEditModal = (data: TradingTimesModel) => {
    setModalData(data);
    setIsModalVisible(true);
  };

  const handleOk = () => {
    setModalData(undefined);
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setModalData(undefined);
    setIsModalVisible(false);
  };

  const formattedTableData = tableData.map((obj) => ({ ...obj }));
  formattedTableData.forEach((element) => {
    element.opening = clearSeconds(element.opening);
    element.closing = clearSeconds(element.closing);
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: TableColumnsType<any> = [
    {
      title: t('common.time.unit.day'),
      dataIndex: 'dayOfWeek',
      key: 'dayOfWeek',
      sorter: (a, b) => sortWeekdays<TradingTimesModel>(a, b, 'dayOfWeek'),
      filters: createTableFilters(tableData, 'dayOfWeek'),
      onFilter: (value, record) => filterByProperty(value, record, 'dayOfWeek'),
    },
    {
      title: t('locations.trading.openingTime'),
      dataIndex: 'opening',
      key: 'opening',
      render: (_, record) => clearSeconds(record.opening),
      sorter: (a, b) => sortTimes(a.opening, b.opening),
      filters: createTableFilters(formattedTableData, 'opening'),
      onFilter: (value, record) => filterByProperty(value, record, 'opening'),
    },
    {
      title: t('locations.trading.closeTime'),
      dataIndex: 'closing',
      key: 'closing',
      render: (_, record) => clearSeconds(record.closing),
      sorter: (a, b) => sortTimes(a.closing, b.closing),
      filters: createTableFilters(formattedTableData, 'closing'),
      onFilter: (value, record) => filterByProperty(value, record, 'closing'),
    },
    {
      title: '',
      dataIndex: 'id',
      width: 10,
      render: (_, data) => (
        <Button size="small" onClick={() => showEditModal(data)}>
          {t('common.buttons.edit')}
        </Button>
      ),
    },
  ];

  return (
    <Col>
      <PosCard
        headerProps={{
          title: t('locations.trading.tradingTimes'),
          icon: faCalendarRange,
          action: (
            <Button
              onClick={showModal}
              icon={<FontAwesomeIcon icon={faPlus} />}
            >
              {t('common.buttons.addNew')}
            </Button>
          ),
        }}
      >
        <PosTable
          id="tradingTimesDetailsTable"
          columns={columns}
          dataSource={tableData}
          isLoading={isLoading}
          fePagination
        />
        {isModalVisible && (
          <CreateEditTradingTimesModal
            handleOk={handleOk}
            handleCancel={handleCancel}
            data={modalData}
          />
        )}
      </PosCard>
    </Col>
  );
};
